import { useState, useEffect, useCallback, useRef, useMemo, type CSSProperties } from 'react';
import { Calendar, Save, RefreshCw, X, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useCrud } from '../../api/hooks/useCrud';
import type { Turma, Materia, Professor } from '../../types/models';
import '../ui/datatable.css';
import { ROUTES } from '../../api/routes';
import {
  materiaTemProfessorDisponivel,
  mergeOcupacaoComGradeLocal,
  professorOcupadoNoSlot,
  professorLecionaMateria,
  type OcupacaoProfessorSlot,
} from '../../pages/admin/grade-conflito';
import { resolveMateriaCorVisual } from '../materias/catalogo-bncc-data';

// Hierarquia de ensino (itens canônicos + matching flexível p/ "1º Ano", "1ª Série", legado EF/EM)
const SERIES_OPTIONS = [
  { group: 'Ed. Infantil', fullName: 'Educação Infantil', items: ['Maternal', 'Jardim I', 'Jardim II'] },
  { group: 'Fund. I', fullName: 'Ensino Fundamental I', items: ['1º Ano', '2º Ano', '3º Ano', '4º Ano', '5º Ano'] },
  { group: 'Fund. II', fullName: 'Ensino Fundamental II', items: ['6º Ano', '7º Ano', '8º Ano', '9º Ano'] },
  { group: 'Médio', fullName: 'Ensino Médio', items: ['1ª Série', '2ª Série', '3ª Série'] },
];

function normalizeSerieKey(serie: string): string {
  return serie
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function nivelDaSerie(serie: string): string {
  const exact = SERIES_OPTIONS.find((g) => g.items.includes(serie));
  if (exact) return exact.group;
  const s = normalizeSerieKey(serie);
  if (/maternal|jardim|infantil|bercario/.test(s)) return 'Ed. Infantil';
  if (/\bem\b|ensino medio|medio\b/.test(s) || /serie/.test(s)) return 'Médio';
  const anoMatch = s.match(/(\d+)\s*ano/);
  const ano = anoMatch ? parseInt(anoMatch[1], 10) : NaN;
  if (!Number.isNaN(ano)) {
    if (/\bef\b|fundamental/.test(s) || !/\bem\b/.test(s)) {
      if (ano >= 1 && ano <= 5) return 'Fund. I';
      if (ano >= 6 && ano <= 9) return 'Fund. II';
    }
  }
  return 'Outros';
}

function ordemSerie(serie: string): number {
  const nivel = nivelDaSerie(serie);
  const gi = SERIES_OPTIONS.findIndex((g) => g.group === nivel);
  const base = (gi >= 0 ? gi : 9) * 100;
  const m = normalizeSerieKey(serie).match(/(\d+)/);
  return base + (m ? parseInt(m[1], 10) : 50);
}

function parseHexColor(hex?: string | null): { r: number; g: number; b: number } | null {
  if (!hex) return null;
  const raw = hex.trim().replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function softMateriaBg(hex: string, whiteRatio = 0.62): string {
  const rgb = parseHexColor(hex);
  if (!rgb) return '#f8fafc';
  const mix = (c: number) => Math.round(c + (255 - c) * whiteRatio);
  return `rgb(${mix(rgb.r)}, ${mix(rgb.g)}, ${mix(rgb.b)})`;
}

function materiaSelectStyle(corVisual?: string | null, selected?: boolean): CSSProperties {
  if (!selected || !corVisual) {
    return {
      fontSize: '0.74rem',
      padding: '5px 6px',
      borderRadius: 5,
      border: '1px solid #e2e8f0',
      backgroundColor: '#f8fafc',
      color: '#94a3b8',
      fontWeight: 500,
      cursor: 'pointer',
      width: '100%',
    };
  }
  return {
    fontSize: '0.74rem',
    padding: '5px 6px',
    borderRadius: 5,
    border: `1px solid ${corVisual}`,
    backgroundColor: softMateriaBg(corVisual),
    color: '#0f172a',
    fontWeight: 700,
    boxShadow: `inset 3px 0 0 0 ${corVisual}`,
    cursor: 'pointer',
    width: '100%',
  };
}

const DIAS = ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA'] as const;
const DIA_LABELS: Record<string, string> = {
  SEGUNDA: 'SEGUNDA', TERCA: 'TERCA', QUARTA: 'QUARTA',
  QUINTA: 'QUINTA', SEXTA: 'SEXTA', SABADO: 'SABADO',
};

interface HorarioSlot {
  id: string; turmaId: string; diaSemana: string; numeroAula: number;
  horaInicio?: string; horaFim?: string; tipo?: string; descricao?: string;
  materiaId?: string | null; professorId?: string | null;
}

interface GradeAssistantProps {
  open: boolean;
  onClose: () => void;
  initialTurmaId?: string | null;
  embedded?: boolean;
}

/** Retorna os 2 primeiros nomes de um nome completo */
function doisNomes(nome: string): string {
  const parts = nome.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[1]}` : parts[0];
}

function nomeProfessorDisplay(nome?: string | null): string {
  if (!nome) return '';
  return nome
    .replace(/^\s*prof\.?\s*/i, '')
    .replace(/^\s*profª\.?\s*/i, '')
    .replace(/^\s*professora?\s+/i, '')
    .trim();
}

/** Calcula duracao em minutos entre dois horarios HH:MM */
function calcDuracao(inicio?: string, fim?: string): string {
  if (!inicio || !fim) return '';
  const [h1, m1] = inicio.split(':').map(Number);
  const [h2, m2] = fim.split(':').map(Number);
  const mins = (h2 * 60 + m2) - (h1 * 60 + m1);
  return mins > 0 ? `${mins} min` : '';
}

export function GradeAssistant({ open, onClose, initialTurmaId, embedded }: GradeAssistantProps) {
  const escolaId = useAuthStore((s) => s.escolaId);
  const { data: turmas } = useCrud<Turma>('turmas');
  const { data: materias } = useCrud<Materia>('materias');
  const { data: professores } = useCrud<Professor>('professores');
  const [ocupacao, setOcupacao] = useState<OcupacaoProfessorSlot[]>([]);

  const turmasAtivas = turmas.filter((t) => t.ativo);
  const materiasAtivas = materias.filter((m) => m.ativo);
  const profsAtivos = professores.filter((p) => p.ativo);

  // ═══ Filtros ═══
  const anoAtual = new Date().getFullYear();
  const [filterAno, setFilterAno] = useState(anoAtual);
  const [filterTurno, setFilterTurno] = useState('');

  // Anos e turnos disponiveis (derivados dos dados)
  const anosDisponiveis = [...new Set(turmasAtivas.map((t) => t.anoLetivo))].sort((a, b) => b - a);
  const turnosDisponiveis: { id: string; nome: string }[] = [];
  const turnoIds = new Set<string>();
  turmasAtivas.forEach((t) => {
    if (t.turnoOperacional && !turnoIds.has(t.turnoOperacional.id)) {
      turnoIds.add(t.turnoOperacional.id);
      turnosDisponiveis.push({ id: t.turnoOperacional.id, nome: t.turnoOperacional.nome });
    }
  });
  // Ordena por lógica temporal: Manhã → Tarde → Noite
  const TURNO_ORDER: Record<string, number> = { manh: 0, tard: 1, noit: 2 };
  turnosDisponiveis.sort((a, b) => {
    const ka = Object.keys(TURNO_ORDER).find(k => a.nome.toLowerCase().includes(k));
    const kb = Object.keys(TURNO_ORDER).find(k => b.nome.toLowerCase().includes(k));
    return (TURNO_ORDER[ka ?? ''] ?? 99) - (TURNO_ORDER[kb ?? ''] ?? 99);
  });

  // Turmas filtradas — memoizado para a referência só mudar quando os filtros mudarem
  const turmasFiltradas = useMemo(() =>
    turmasAtivas.filter((t) => {
      if (t.anoLetivo !== filterAno) return false;
      if (filterTurno && t.turnoOperacionalId !== filterTurno) return false;
      return true;
    })
  , [turmasAtivas, filterAno, filterTurno]);

  // ── Navegação hierárquica 3 níveis ──
  const [selectedNivel, setSelectedNivel] = useState('');
  const [selectedSerie, setSelectedSerie] = useState('');
  const [selectedTurmaId, setSelectedTurmaId] = useState<string | null>(null);

  // Cache de seleção por turno — memoriza ultimo nivel+serie+turma de cada turno
  const selectionCacheRef = useRef<Map<string, { nivel: string; serie: string; turmaId: string }>>(new Map());
  // Cache por turno+serie — memoriza ultima turma selecionada de cada serie
  const serieCacheRef = useRef<Map<string, string>>(new Map());
  // Helper: chave para serieCacheRef
  const serieKey = (turno: string, serie: string) => `${turno}::${serie}`;

  // Derivados de navegação (todos via useMemo, sem estado extra)
  const niveisDisponiveis = useMemo(() => {
    const grupos = new Set(turmasFiltradas.map((t) => nivelDaSerie(t.serie)));
    const known = SERIES_OPTIONS.filter((g) => grupos.has(g.group));
    if (grupos.has('Outros')) {
      return [...known, { group: 'Outros', fullName: 'Outras séries', items: [] as string[] }];
    }
    return known;
  }, [turmasFiltradas]);

  // Séries reais das turmas filtradas (aceita "1 Ano EF" / "1 Ano EM" do demo)
  const seriesDoNivel = useMemo(() => {
    const series = [
      ...new Set(
        turmasFiltradas
          .filter((t) => !selectedNivel || nivelDaSerie(t.serie) === selectedNivel)
          .map((t) => t.serie),
      ),
    ];
    return series.sort((a, b) => ordemSerie(a) - ordemSerie(b) || a.localeCompare(b, 'pt-BR'));
  }, [turmasFiltradas, selectedNivel]);

  // Quando selectedNivel='' mostra tudo; quando selectedSerie='' mostra todo o nivel
  const turmasDaSerie = useMemo(() => {
    return turmasFiltradas
      .filter((t) => {
        if (selectedNivel && nivelDaSerie(t.serie) !== selectedNivel) return false;
        if (selectedSerie && t.serie !== selectedSerie) return false;
        return true;
      })
      .sort((a, b) => {
        const oa = ordemSerie(a.serie);
        const ob = ordemSerie(b.serie);
        if (oa !== ob) return oa - ob;
        return a.nome.localeCompare(b.nome, 'pt-BR');
      });
  }, [turmasFiltradas, selectedNivel, selectedSerie]);

  // Lista flat ordenada por hierarquia (para prev/next global)
  const turmasOrdenadas = useMemo(
    () =>
      [...turmasFiltradas].sort(
        (a, b) =>
          ordemSerie(a.serie) - ordemSerie(b.serie) || a.nome.localeCompare(b.nome, 'pt-BR'),
      ),
    [turmasFiltradas],
  );

  const selectedTurma = turmasFiltradas.find(t => t.id === selectedTurmaId) ?? null;
  const flatIndex = turmasOrdenadas.findIndex(t => t.id === selectedTurmaId);

  // Scroll refs para cada nível
  const nivelRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const serieRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const turmaRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const scrollToRef = (map: React.MutableRefObject<Map<string, HTMLButtonElement>>, key: string) => {
    setTimeout(() => map.current.get(key)?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' }), 50);
  };

  // Selecionar turma completa (seta todos os 3 níveis)
  const selectTurma = useCallback((turma: Turma, skipDirtyCheck = false) => {
    const nivel = nivelDaSerie(turma.serie);
    setSelectedNivel(nivel);
    setSelectedSerie(turma.serie);
    setSelectedTurmaId(turma.id);
    scrollToRef(nivelRefs, nivel);
    scrollToRef(serieRefs, turma.serie);
    scrollToRef(turmaRefs, turma.id);
  }, []);

  const [horarios, setHorarios] = useState<HorarioSlot[]>([]);
  const [loadingGrade, setLoadingGrade] = useState(false);
  const [saving, setSaving] = useState(false);
  const snapshotRef = useRef('');
  const [savedTurmas, setSavedTurmas] = useState<Set<string>>(new Set());
  const pendingTurmaIdRef = useRef<string | null>(null);

  // Overlay de alterações não salvas
  const [showDirtyOverlay, setShowDirtyOverlay] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  /** Intercepta qualquer ação de navegação: se sujo, mostra overlay; senão executa direto */
  const guardedAction = (action: () => void) => {
    if (isDirty) {
      pendingActionRef.current = action;
      setShowDirtyOverlay(true);
    } else {
      action();
    }
  };

  const dismissOverlay = () => { setShowDirtyOverlay(false); pendingActionRef.current = null; };

  const discardAndProceed = () => {
    snapshotRef.current = JSON.stringify(horarios.map(h => ({ m: h.materiaId || '', p: h.professorId || '' })));
    const action = pendingActionRef.current;
    dismissOverlay();
    action?.();
  };

  const saveAndProceed = async () => {
    const ok = await salvarGrade();
    if (ok) {
      const action = pendingActionRef.current;
      dismissOverlay();
      action?.();
    }
  };

  const currentSnapshot = JSON.stringify(
    horarios.map((h) => ({ m: h.materiaId || '', p: h.professorId || '' }))
  );
  const isDirty = currentSnapshot !== snapshotRef.current && horarios.length > 0;

  const fetchGrade = useCallback(async (turmaId: string) => {
    if (!escolaId || !turmaId) return;
    setLoadingGrade(true);
    try {
      const { data } = await api.get(ROUTES.horarios.porTurma(escolaId, turmaId));
      const slots = Array.isArray(data) ? data : [];
      setHorarios(slots);
      snapshotRef.current = JSON.stringify(
        slots.map((h: HorarioSlot) => ({ m: h.materiaId || '', p: h.professorId || '' }))
      );
    } catch {
      setHorarios([]);
      snapshotRef.current = '[]';
    }
    setLoadingGrade(false);
  }, [escolaId]);

  const reloadOcupacao = useCallback(async () => {
    if (!escolaId) return;
    try {
      const { data } = await api.get(ROUTES.horarios.ocupacao(escolaId));
      setOcupacao(Array.isArray(data) ? data : []);
    } catch {
      setOcupacao([]);
    }
  }, [escolaId]);

  useEffect(() => {
    if (open && selectedTurma) fetchGrade(selectedTurma.id);
  }, [open, selectedTurma?.id, fetchGrade]);

  useEffect(() => {
    if (open && escolaId) reloadOcupacao();
  }, [open, escolaId, reloadOcupacao]);

  useEffect(() => {
    if (open) {
      setSavedTurmas(new Set());
      if (initialTurmaId) {
        const turma = turmasAtivas.find(t => t.id === initialTurmaId);
        if (turma) {
          setFilterAno(turma.anoLetivo);
          setFilterTurno(turma.turnoOperacionalId || turnosDisponiveis[0]?.id || '');
          pendingTurmaIdRef.current = initialTurmaId;
        } else {
          setFilterAno(anoAtual);
          setFilterTurno(turnosDisponiveis[0]?.id || '');
          pendingTurmaIdRef.current = null;
          if (turmasOrdenadas.length) selectTurma(turmasOrdenadas[0]);
        }
      } else {
        setFilterAno(anoAtual);
        setFilterTurno(turnosDisponiveis[0]?.id || '');
        pendingTurmaIdRef.current = null;
        if (turmasOrdenadas.length) selectTurma(turmasOrdenadas[0]);
      }
    }
  }, [open, initialTurmaId]);

  // Resolve a turma após filtros serem aplicados (apenas pending — sem auto-reset)
  useEffect(() => {
    if (pendingTurmaIdRef.current) {
      const turma = turmasFiltradas.find(t => t.id === pendingTurmaIdRef.current);
      if (turma) { selectTurma(turma); pendingTurmaIdRef.current = null; }
    }
  }, [turmasFiltradas]);

  // Valida selectedNivel após troca de turno: se o nivel não existir no novo turno, reseta para '' (Todas)
  useEffect(() => {
    if (selectedNivel) {
      const grupos = new Set(turmasFiltradas.map(t => nivelDaSerie(t.serie)));
      if (!grupos.has(selectedNivel)) {
        setSelectedNivel('');
        setSelectedSerie('');
      }
    }
  }, [filterTurno]);

  // ═══ Grade operations ═══
  const autoDistribuirSlots = (baseSlots: HorarioSlot[]): {
    slots: HorarioSlot[];
    preenchidos: number;
    novos: number;
    totalAulas: number;
    motivo?: string;
  } => {
    const slotsAulas = baseSlots.filter((s) => s.tipo !== 'INTERVALO');
    const totalAulas = slotsAulas.length;

    if (!selectedTurma) {
      return { slots: baseSlots, preenchidos: 0, novos: 0, totalAulas, motivo: 'Nenhuma turma selecionada.' };
    }
    if (materiasAtivas.length === 0) {
      return { slots: baseSlots, preenchidos: 0, novos: 0, totalAulas, motivo: 'Nenhuma matéria ativa encontrada nesta escola.' };
    }
    if (profsAtivos.length === 0) {
      return { slots: baseSlots, preenchidos: 0, novos: 0, totalAulas, motivo: 'Nenhum professor ativo cadastrado nesta escola.' };
    }

    const turnoOpId = selectedTurma.turnoOperacionalId ?? selectedTurma.turnoOperacional?.id ?? null;

    // Mapeia matérias com seus professores habilitados (via p.materias ou p.turmas)
    const materiaProfMap = materiasAtivas
      .map((m) => {
        const profs = profsAtivos.filter((p) => professorLecionaMateria(p, m.id));
        return { materia: m, profs };
      })
      .filter((item) => item.profs.length > 0);

    if (materiaProfMap.length === 0) {
      return {
        slots: baseSlots,
        preenchidos: 0,
        novos: 0,
        totalAulas,
        motivo: 'Não há professores vinculados às disciplinas. Cadastre as matérias dos professores em Cadastros Base > Professores.',
      };
    }

    // Inicializa lista acumulada com slots que já estão preenchidos com matéria + professor
    const slotsAlocados: HorarioSlot[] = baseSlots.filter(
      (s) => s.tipo !== 'INTERVALO' && s.materiaId && s.professorId,
    );

    // Contagem de aulas por matéria para balanceamento curricular
    const aulasPorMateria = new Map<string, number>();
    const aulasPorMateriaDia = new Map<string, number>(); // `${materiaId}_${diaSemana}`

    for (const alocado of slotsAlocados) {
      if (alocado.materiaId) {
        aulasPorMateria.set(alocado.materiaId, (aulasPorMateria.get(alocado.materiaId) || 0) + 1);
        const keyDia = `${alocado.materiaId}_${alocado.diaSemana}`;
        aulasPorMateriaDia.set(keyDia, (aulasPorMateriaDia.get(keyDia) || 0) + 1);
      }
    }

    let novosPreenchidos = 0;
    const resultado: HorarioSlot[] = [];

    for (const slot of baseSlots) {
      if (slot.tipo !== 'AULA') {
        resultado.push(slot);
        continue;
      }

      // Se já possui matéria e professor válidos, preserva
      if (slot.materiaId && slot.professorId) {
        resultado.push(slot);
        continue;
      }

      // Ocupação efetiva acumulada até o momento (inclui ocupação de outras turmas + aulas já alocadas nesta turma)
      const ocupacaoAtual = mergeOcupacaoComGradeLocal(
        ocupacao,
        slotsAlocados,
        selectedTurma.id,
        turnoOpId,
        selectedTurma.nome,
      );

      let assigned = false;

      // 1. Se o slot já tinha matéria selecionada manualmente, tenta achar professor livre para ela primeiro
      if (slot.materiaId) {
        const itemMat = materiaProfMap.find((item) => item.materia.id === slot.materiaId);
        if (itemMat) {
          const freeProf = itemMat.profs.find((p) => !professorOcupadoNoSlot(ocupacaoAtual, {
            professorId: p.id,
            diaSemana: slot.diaSemana,
            numeroAula: slot.numeroAula,
            horaInicio: slot.horaInicio,
            horaFim: slot.horaFim,
            excludeSlot: {
              turmaId: selectedTurma.id,
              diaSemana: slot.diaSemana,
              numeroAula: slot.numeroAula,
            },
            turnoOperacionalId: turnoOpId,
          }));

          if (freeProf) {
            const newSlot: HorarioSlot = {
              ...slot,
              materiaId: itemMat.materia.id,
              professorId: freeProf.id,
            };
            resultado.push(newSlot);
            slotsAlocados.push(newSlot);
            novosPreenchidos++;
            aulasPorMateria.set(itemMat.materia.id, (aulasPorMateria.get(itemMat.materia.id) || 0) + 1);
            const keyDia = `${itemMat.materia.id}_${slot.diaSemana}`;
            aulasPorMateriaDia.set(keyDia, (aulasPorMateriaDia.get(keyDia) || 0) + 1);
            assigned = true;
          }
        }
      }

      if (assigned) continue;

      // 2. Ordena matérias por menor quantidade de aulas na semana para distribuição balanceada
      const materiasCandidatas = [...materiaProfMap].sort((a, b) => {
        const countA = aulasPorMateria.get(a.materia.id) || 0;
        const countB = aulasPorMateria.get(b.materia.id) || 0;
        return countA - countB;
      });

      // Tentativa 2a: com limite pedagógico de no máximo 2 aulas da mesma matéria no mesmo dia
      for (const item of materiasCandidatas) {
        const aulasNoDia = aulasPorMateriaDia.get(`${item.materia.id}_${slot.diaSemana}`) || 0;
        if (aulasNoDia >= 2 && materiasCandidatas.length > 2) {
          continue;
        }

        const freeProf = item.profs.find((p) => !professorOcupadoNoSlot(ocupacaoAtual, {
          professorId: p.id,
          diaSemana: slot.diaSemana,
          numeroAula: slot.numeroAula,
          horaInicio: slot.horaInicio,
          horaFim: slot.horaFim,
          excludeSlot: {
            turmaId: selectedTurma.id,
            diaSemana: slot.diaSemana,
            numeroAula: slot.numeroAula,
          },
          turnoOperacionalId: turnoOpId,
        }));

        if (freeProf) {
          const newSlot: HorarioSlot = {
            ...slot,
            materiaId: item.materia.id,
            professorId: freeProf.id,
          };
          resultado.push(newSlot);
          slotsAlocados.push(newSlot);
          novosPreenchidos++;
          aulasPorMateria.set(item.materia.id, (aulasPorMateria.get(item.materia.id) || 0) + 1);
          const keyDia = `${item.materia.id}_${slot.diaSemana}`;
          aulasPorMateriaDia.set(keyDia, (aulasPorMateriaDia.get(keyDia) || 0) + 1);
          assigned = true;
          break;
        }
      }

      // Tentativa 2b: se não encontrou com limite diário, busca qualquer professor livre sem limite diário
      if (!assigned) {
        for (const item of materiasCandidatas) {
          const freeProf = item.profs.find((p) => !professorOcupadoNoSlot(ocupacaoAtual, {
            professorId: p.id,
            diaSemana: slot.diaSemana,
            numeroAula: slot.numeroAula,
            horaInicio: slot.horaInicio,
            horaFim: slot.horaFim,
            excludeSlot: {
              turmaId: selectedTurma.id,
              diaSemana: slot.diaSemana,
              numeroAula: slot.numeroAula,
            },
            turnoOperacionalId: turnoOpId,
          }));

          if (freeProf) {
            const newSlot: HorarioSlot = {
              ...slot,
              materiaId: item.materia.id,
              professorId: freeProf.id,
            };
            resultado.push(newSlot);
            slotsAlocados.push(newSlot);
            novosPreenchidos++;
            aulasPorMateria.set(item.materia.id, (aulasPorMateria.get(item.materia.id) || 0) + 1);
            const keyDia = `${item.materia.id}_${slot.diaSemana}`;
            aulasPorMateriaDia.set(keyDia, (aulasPorMateriaDia.get(keyDia) || 0) + 1);
            assigned = true;
            break;
          }
        }
      }

      if (!assigned) {
        resultado.push(slot);
      }
    }

    const preenchidosTotal = resultado.filter(
      (s) => s.tipo !== 'INTERVALO' && s.materiaId && s.professorId,
    ).length;

    return {
      slots: resultado,
      preenchidos: preenchidosTotal,
      novos: novosPreenchidos,
      totalAulas,
    };
  };

  // Gerar grade usa o template do turno e preenche matérias e professores sem choque
  const gerarGrade = async () => {
    if (!selectedTurma || !escolaId) return;
    try {
      let slots = horarios;
      if (slots.length === 0) {
        const { data } = await api.post(ROUTES.horarios.redefinir(escolaId, selectedTurma.id));
        slots = Array.isArray(data) ? data : [];
      }
      const resultado = autoDistribuirSlots(slots);
      setHorarios(resultado.slots);

      if (resultado.motivo) {
        toast.error(resultado.motivo);
        return;
      }

      if (resultado.novos === 0) {
        if (resultado.preenchidos === resultado.totalAulas && resultado.totalAulas > 0) {
          toast.info('Todos os horários desta turma já estão preenchidos!');
        } else {
          const nomeTurno = selectedTurma.turnoOperacional?.nome || 'neste turno';
          toast.error(
            `Não foi possível preencher os horários: os professores das matérias já estão ocupados em outras turmas no turno (${nomeTurno}) ou não há professores disponíveis para esta série/turma sem choque de horários.`,
          );
        }
      } else if (resultado.preenchidos === resultado.totalAulas) {
        toast.success(`Grade preenchida com sucesso! Todas as ${resultado.totalAulas} aulas foram distribuídas sem choques.`);
      } else {
        const pendentes = resultado.totalAulas - resultado.preenchidos;
        const nomeTurno = selectedTurma.turnoOperacional?.nome || 'no turno';
        toast.warning(
          `${resultado.novos} horário(s) preenchido(s). ${pendentes} aula(s) ficaram vagas por falta de professores livres ${nomeTurno} sem conflito de horário.`,
        );
      }
    } catch {
      toast.error('Erro ao gerar grade');
    }
  };

  const updateSlot = (slotId: string, field: 'materiaId' | 'professorId', value: string) => {
    setHorarios((prev) => prev.map((h) => {
      if (h.id !== slotId) return h;
      if (field === 'materiaId') {
        // Ao mudar materia, limpar professor se nao pertence a nova materia
        const newMateriaId = value || null;
        const currentProfId = h.professorId;
        let keepProf = false;
        if (currentProfId && newMateriaId) {
          const prof = profsAtivos.find(p => p.id === currentProfId);
          keepProf = prof?.materias?.some(m => m.id === newMateriaId) || false;
        }
        return { ...h, materiaId: newMateriaId, professorId: keepProf ? currentProfId : null };
      }
      if (field === 'professorId' && value && selectedTurma) {
        const turnoOpId =
          selectedTurma.turnoOperacionalId ?? selectedTurma.turnoOperacional?.id ?? null;
        const ocupacaoEfetiva = mergeOcupacaoComGradeLocal(
          ocupacao,
          prev,
          selectedTurma.id,
          turnoOpId,
          selectedTurma.nome,
        );
        const conflito = professorOcupadoNoSlot(ocupacaoEfetiva, {
          professorId: value,
          diaSemana: h.diaSemana,
          numeroAula: h.numeroAula,
          horaInicio: h.horaInicio,
          horaFim: h.horaFim,
          excludeSlot: {
            turmaId: selectedTurma.id,
            diaSemana: h.diaSemana,
            numeroAula: h.numeroAula,
          },
          turnoOperacionalId: turnoOpId,
        });
        if (conflito) {
          toast.error(
            `Professor ocupado em ${conflito.turmaNome || 'outra turma'}` +
              (conflito.horaInicio ? ` (${conflito.horaInicio})` : ''),
          );
          return h;
        }
      }
      return { ...h, [field]: value || null };
    }));
  };


  const salvarGrade = async (): Promise<boolean> => {
    if (!selectedTurma || !escolaId) return false;

    const semProfessor = horarios.find(h => h.materiaId && !h.professorId);
    if (semProfessor) {
      toast.error('Há matérias na grade sem professor vinculado.');
      return false;
    }

    const conflito = horarios.find(h => {
      if (!h.professorId) return false;
      return !!professorOcupadoNoSlot(ocupacaoEfetiva, {
        professorId: h.professorId,
        diaSemana: h.diaSemana,
        numeroAula: h.numeroAula,
        horaInicio: h.horaInicio || undefined,
        horaFim: h.horaFim || undefined,
        excludeSlot: {
          turmaId: selectedTurma.id,
          diaSemana: h.diaSemana,
          numeroAula: h.numeroAula,
        }
      });
    });

    if (conflito) {
      toast.error('Existem professores com conflito de horários nesta grade.');
      return false;
    }

    setSaving(true);
    try {
      const payload = horarios.map((h) => ({
        diaSemana: h.diaSemana, numeroAula: h.numeroAula,
        materiaId: h.materiaId || null, professorId: h.professorId || null,
        horaInicio: h.horaInicio || null, horaFim: h.horaFim || null,
      }));
      await api.put(ROUTES.horarios.bulk(escolaId, selectedTurma.id), { horarios: payload });
      snapshotRef.current = currentSnapshot;
      setSavedTurmas((prev) => new Set(prev).add(selectedTurma.id));
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      toast.success(`Grade da turma ${selectedTurma.nome} salva!`);
      await reloadOcupacao();
      setSaving(false);
      return true;
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { message?: string | string[] | { message?: string } } };
      };
      const msg = ax.response?.data?.message;
      const text =
        typeof msg === 'string'
          ? msg
          : Array.isArray(msg)
            ? msg.join('; ')
            : msg && typeof msg === 'object'
              ? msg.message
              : undefined;
      toast.error(text || 'Erro ao salvar grade');
      setSaving(false);
      return false;
    }
  };

  const navigateTo = (turma: Turma) => {
    if (turma.id === selectedTurmaId) return;
    guardedAction(() => {
      // Sempre grava a turma escolhida no cache da serie atual
      if (selectedSerie) {
        serieCacheRef.current.set(serieKey(filterTurno, turma.serie), turma.id);
      }
      if (!selectedNivel) {
        // Modo "Todas as Series": preserva o dropdown, so muda serie e turma
        setSelectedSerie(turma.serie);
        setSelectedTurmaId(turma.id);
        scrollToRef(serieRefs, turma.serie);
        scrollToRef(turmaRefs, turma.id);
      } else {
        selectTurma(turma);
      }
    });
  };

  const handleClose = () => guardedAction(() => onClose());

  const goNext = () => { const i = flatIndex; if (i < turmasOrdenadas.length - 1) navigateTo(turmasOrdenadas[i + 1]); };
  const goPrev = () => { const i = flatIndex; if (i > 0) navigateTo(turmasOrdenadas[i - 1]); };

  const handleFilterChange = (tipo: 'ano' | 'turno', valor: string) => {
    guardedAction(() => {
      if (tipo === 'turno') {
        // Salva seleção ATUAL no cache do turno de saída (incluindo nivel atual)
        if (filterTurno) {
          selectionCacheRef.current.set(filterTurno, {
            nivel: selectedNivel,
            serie: selectedSerie,
            turmaId: selectedTurmaId ?? '',
          });
        }
        // Restaura cache do turno de destino (se existir)
        const cached = selectionCacheRef.current.get(valor);
        if (cached) {
          setSelectedNivel(cached.nivel);
          setSelectedSerie(cached.serie);
          setSelectedTurmaId(cached.turmaId || null);
        } else {
          setSelectedNivel('');
          setSelectedSerie('');
          setSelectedTurmaId(null);
        }
        setFilterTurno(valor);
      } else {
        setSelectedNivel(''); setSelectedSerie(''); setSelectedTurmaId(null);
        setFilterAno(Number(valor));
      }
    });
  };

  /** O dropdown de nivel filtra as series exibidas — nao navega para turma */
  const navigateToNivel = (grupo: string) => {
    guardedAction(() => {
      setSelectedNivel(grupo);
      // limpa serie apenas se a serie atual nao pertence ao novo nivel
      const novasSerie = (grupo && selectedSerie && nivelDaSerie(selectedSerie) !== grupo) ? '' : selectedSerie;
      if (novasSerie !== selectedSerie) setSelectedSerie('');

      // ✅ Grava imediatamente no cache do turno atual — não espera a troca de turno
      if (filterTurno) {
        const prev = selectionCacheRef.current.get(filterTurno) ?? { nivel: '', serie: '', turmaId: '' };
        selectionCacheRef.current.set(filterTurno, {
          ...prev,
          nivel: grupo,
          serie: novasSerie,
        });
      }
    });
  };

  const navigateToSerie = (serie: string) => {
    guardedAction(() => {
      // Salva a turma atual no cache da serie que estamos saindo
      if (selectedSerie && selectedTurmaId) {
        serieCacheRef.current.set(serieKey(filterTurno, selectedSerie), selectedTurmaId);
      }

      const turmasDaSerie = turmasFiltradas
        .filter(t => t.serie === serie)
        .sort((a, b) => a.nome.localeCompare(b.nome));

      // Restaura ultima turma da serie destino (ou usa a primeira)
      const cachedId = serieCacheRef.current.get(serieKey(filterTurno, serie));
      const targetTurma = (cachedId && turmasDaSerie.find(t => t.id === cachedId))
        || turmasDaSerie[0];

      if (!selectedNivel) {
        // Modo "Todas as Series": nao muda o dropdown
        setSelectedSerie(serie);
        if (targetTurma) {
          setSelectedTurmaId(targetTurma.id);
          scrollToRef(serieRefs, serie);
          scrollToRef(turmaRefs, targetTurma.id);
        } else {
          setSelectedTurmaId(null);
        }
      } else {
        // Modo nivel especifico: comportamento normal
        if (targetTurma) selectTurma(targetTurma);
        else { setSelectedSerie(serie); setSelectedTurmaId(null); }
      }
    });
  };

  // Grade data
  const diasPresentes = [...new Set(horarios.map((h) => h.diaSemana))].sort((a, b) => DIAS.indexOf(a as any) - DIAS.indexOf(b as any));
  const maxAulas = Math.max(...horarios.map((h) => h.numeroAula), 0);
  const slotsPreenchidos = horarios.filter((h) => h.materiaId && h.tipo !== 'INTERVALO').length;
  const totalAulas = horarios.filter(h => h.tipo !== 'INTERVALO').length;

  const ocupacaoEfetiva = useMemo(() => {
    if (!selectedTurma) return ocupacao;
    const turnoOpId =
      selectedTurma.turnoOperacionalId ?? selectedTurma.turnoOperacional?.id ?? null;
    const ocupacaoComTurno = ocupacao.map((o) => {
      if (o.turnoOperacionalId) return o;
      const tRef = turmas.find((t) => t.id === o.turmaId);
      return {
        ...o,
        turnoOperacionalId: tRef?.turnoOperacionalId ?? tRef?.turnoOperacional?.id ?? null,
      };
    });
    return mergeOcupacaoComGradeLocal(
      ocupacaoComTurno,
      horarios.filter((h) => h.tipo !== 'INTERVALO'),
      selectedTurma.id,
      turnoOpId,
      selectedTurma.nome,
    );
  }, [ocupacao, horarios, selectedTurma, turmas]);

  const gradeTemErros = useMemo(() => {
    return horarios.some(h => {
      if (h.materiaId && !h.professorId) return true;
      if (h.materiaId && h.professorId && selectedTurma) {
        const diaNum = { SEGUNDA: 1, TERCA: 2, QUARTA: 3, QUINTA: 4, SEXTA: 5, SABADO: 6 }[h.diaSemana as string] as number;
        const turnoOpId = selectedTurma.turnoOperacionalId || selectedTurma.turnoOperacional?.id;
        const conflito = professorOcupadoNoSlot(ocupacaoEfetiva, {
          professorId: h.professorId,
          diaSemana: h.diaSemana,
          numeroAula: h.numeroAula,
          horaInicio: h.horaInicio || undefined,
          horaFim: h.horaFim || undefined,
          excludeSlot: {
            turmaId: selectedTurma.id,
            diaSemana: h.diaSemana,
            numeroAula: h.numeroAula,
          },
          turnoOperacionalId: turnoOpId,
        });
        if (conflito) return true;
      }
      return false;
    });
  }, [horarios, ocupacaoEfetiva, selectedTurma]);

  // Filtrar professores: vínculo com a matéria + livre no horário (sem conflito cross-turma)
  const getProfsForMateria = (slot: HorarioSlot) => {
    const materiaId = slot.materiaId;
    if (!materiaId) {
      return slot.professorId
        ? profsAtivos.filter((p) => p.id === slot.professorId)
        : [];
    }
    const turnoOpId =
      selectedTurma?.turnoOperacionalId ?? selectedTurma?.turnoOperacional?.id ?? null;
    return profsAtivos.filter((p) => {
      if (p.id === slot.professorId) return true; // já atribuído no slot
      if (!professorLecionaMateria(p, materiaId)) return false;
      if (!selectedTurma) return true;
      return !professorOcupadoNoSlot(ocupacaoEfetiva, {
        professorId: p.id,
        diaSemana: slot.diaSemana,
        numeroAula: slot.numeroAula,
        horaInicio: slot.horaInicio,
        horaFim: slot.horaFim,
        excludeSlot: {
          turmaId: selectedTurma.id,
          diaSemana: slot.diaSemana,
          numeroAula: slot.numeroAula,
        },
        turnoOperacionalId: turnoOpId,
      });
    });
  };

  const isInsideWizard = useMemo(() => {
    return !!document.getElementById('sw-actual-page-container');
  }, [open]);

  if (!open) return null;

  const content = (
    <div style={{
      background: 'var(--bg-main, #f8fafc)', borderRadius: embedded ? 0 : 16,
      width: '100%', maxWidth: embedded ? '100%' : 1200,
      height: '100%', maxHeight: embedded ? '100%' : '94vh',
      display: 'flex', flexDirection: 'column',
      boxShadow: embedded ? 'none' : '0 25px 50px rgba(0,0,0,0.25)', overflow: 'hidden',
    }}>

        {/* HEADER */}
        <div style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
          padding: '10px 16px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', color: 'white', flexShrink: 0, gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <Calendar size={20} className="shrink-0" />
            <h2 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Assistente de Grade de Horários
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, background: 'rgba(255,255,255,0.18)', padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
              {savedTurmas.size}/{turmasOrdenadas.length} salvas
            </span>
            <button onClick={handleClose} title="Fechar assistente" style={{
              background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: 8,
              width: 32, height: 32, cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', color: 'white',
            }}><X size={16} /></button>
          </div>
        </div>

        {/* NAVEGAÇÃO: Série tabs (esquerda) + Nível dropdown (direita) */}
        <div style={{ flexShrink: 0, background: 'var(--bg-card, white)', borderBottom: '1px solid var(--border-color, #e5e7eb)', overflowX: 'auto' }}>
          <style>{`
            .ga-serie-scroll::-webkit-scrollbar { height: 4px; }
            .ga-serie-scroll::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 99px; }
            .ga-serie-scroll::-webkit-scrollbar-thumb {
              background: linear-gradient(90deg, #2563eb, #6366f1);
              border-radius: 99px;
            }
            .ga-serie-scroll::-webkit-scrollbar-thumb:hover {
              background: linear-gradient(90deg, #1d4ed8, #4f46e5);
            }
            .ga-serie-scroll { scrollbar-width: thin; scrollbar-color: #3b82f6 #f1f5f9; }
            /* Turno: pills no desktop, select no mobile */
            .ga-turno-pills  { display: flex; }
            .ga-turno-select { display: none; }
            @media (max-width: 639px) {
              .ga-turno-pills  { display: none !important; }
              .ga-turno-select { display: block !important; }
            }
          `}</style>

          {/* Linha 1: Ano | Turno | Ensino | [séries → direita] */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', gap: 6, minWidth: 'max-content' }}>

            {/* Dropdown Ano Letivo — cor dinâmica por contexto */}
            {(() => {
              const isPast   = filterAno < anoAtual;
              const isFuture = filterAno > anoAtual;
              const bg     = isPast   ? 'linear-gradient(135deg,#b45309,#f59e0b)'
                           : isFuture ? 'linear-gradient(135deg,#6d28d9,#a855f7)'
                           :            'linear-gradient(135deg,#2563eb,#3b82f6)';
              const border = isPast   ? '#f59e0b'
                           : isFuture ? '#a855f7'
                           :            '#3b82f6';
              return (
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  {/* Ícone contextual à esquerda dentro do wrapper */}
                  {isPast && (
                    <AlertTriangle size={11} style={{
                      position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)',
                      color: 'white', pointerEvents: 'none', zIndex: 1,
                    }} />
                  )}
                  {isFuture && (
                    <Calendar size={11} style={{
                      position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)',
                      color: 'white', pointerEvents: 'none', zIndex: 1,
                    }} />
                  )}
                  <select
                    value={filterAno}
                    onChange={e => handleFilterChange('ano', e.target.value)}
                    style={{
                      height: 28, fontSize: '0.76rem', fontWeight: 700,
                      paddingLeft: (isPast || isFuture) ? 22 : 10,
                      paddingRight: 10,
                      borderRadius: 6, border: `1.5px solid ${border}`,
                      background: bg,
                      color: 'white', cursor: 'pointer', outline: 'none',
                      lineHeight: '28px', appearance: 'auto',
                    }}
                  >
                    {anosDisponiveis.map(a => (
                      <option key={a} value={a} style={{ color: '#1e293b', background: 'white' }}>{a}</option>
                    ))}
                  </select>
                </div>
              );
            })()}

            {/* Conector: seta cinza */}
            <ChevronRight size={14} style={{ color: '#6b7280', flexShrink: 0 }} />

            {/* TURNO — Pills no desktop, Select no mobile */}
            {/* Pills (desktop) */}
            <div className="ga-turno-pills" style={{ gap: 4, alignItems: 'center', flexShrink: 0 }}>
              {turnosDisponiveis.map(t => {
                const isAct = t.id === filterTurno;
                return (
                  <button
                    key={t.id}
                    onClick={() => handleFilterChange('turno', t.id)}
                    style={{
                      height: 28, padding: '0 12px', borderRadius: 6, whiteSpace: 'nowrap',
                      fontWeight: isAct ? 700 : 600, fontSize: '0.76rem',
                      cursor: 'pointer', flexShrink: 0, transition: 'all .15s',
                      border: isAct ? 'none' : '1.5px solid #3b82f6',
                      background: isAct ? 'linear-gradient(135deg,#2563eb,#3b82f6)' : 'transparent',
                      color: isAct ? 'white' : '#3b82f6',
                      boxShadow: isAct ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
                    }}
                  >
                    {t.nome}
                  </button>
                );
              })}
            </div>
            {/* Select (mobile fallback) */}
            <select
              className="ga-turno-select"
              value={filterTurno}
              onChange={e => handleFilterChange('turno', e.target.value)}
              style={{
                height: 28, fontSize: '0.76rem', fontWeight: 700, padding: '0 10px',
                borderRadius: 6, border: '1.5px solid #3b82f6',
                background: 'linear-gradient(135deg,#2563eb,#3b82f6)',
                color: 'white', cursor: 'pointer', outline: 'none', flexShrink: 0,
                lineHeight: '28px', appearance: 'auto',
              }}
            >
              {turnosDisponiveis.map(t => (
                <option key={t.id} value={t.id} style={{ color: '#1e293b', background: 'white' }}>{t.nome}</option>
              ))}
            </select>

            {/* Conector: seta cinza */}
            <ChevronRight size={14} style={{ color: '#6b7280', flexShrink: 0 }} />

            {/* Dropdown Ensino / Todas as Séries */}
            <select
              value={selectedNivel}
              onChange={e => navigateToNivel(e.target.value)}
              style={{
                height: 28, fontSize: '0.76rem', fontWeight: 700, padding: '0 10px',
                borderRadius: 6, border: '1.5px solid #3b82f6',
                background: 'linear-gradient(135deg,#2563eb,#3b82f6)',
                color: 'white', cursor: 'pointer', outline: 'none', flexShrink: 0,
                lineHeight: '28px', appearance: 'auto',
              }}
            >
              <option value="" style={{ color: '#1e293b', background: 'white' }}>Todas as Séries</option>
              {niveisDisponiveis.map(g => (
                <option key={g.group} value={g.group} style={{ color: '#1e293b', background: 'white' }}>{g.fullName}</option>
              ))}
            </select>

            {/* Conector: seta cinza */}
            <ChevronRight size={14} style={{ color: '#6b7280', flexShrink: 0 }} />

            {/* Séries: espaçador empurra para direita; colapsa quando scroll ativo */}
            <div className="ga-serie-scroll" style={{ display: 'flex', gap: 4, overflowX: 'auto', flex: 1, minWidth: 0, paddingBottom: 6 }}>
              {/* Espaçador: ocupa espaço livre → tabs vão para direita. Colapsa quando overflow → scroll funciona */}
              <div style={{ flex: '1 1 0', minWidth: 0, flexShrink: 1 }} />
              {seriesDoNivel.map(serie => {
                const isAct = serie === selectedSerie;
                return (
                   <button key={serie}
                    ref={el => { if (el) void serieRefs.current.set(serie, el); else void serieRefs.current.delete(serie); }}
                    onClick={() => navigateToSerie(serie)}
                    style={{
                      height: 28, padding: '0 12px', borderRadius: 6, whiteSpace: 'nowrap',
                      fontWeight: isAct ? 700 : 500, fontSize: '0.76rem',
                      cursor: 'pointer', flexShrink: 0, transition: 'all .15s',
                      border: isAct ? 'none' : '1px solid var(--border-color, #e5e7eb)',
                      background: isAct ? 'linear-gradient(135deg,#2563eb,#3b82f6)' : 'transparent',
                      color: isAct ? 'white' : '#6b7280',
                      boxShadow: isAct ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
                    }}>
                    {serie}
                  </button>
                );
              })}
            </div>
          </div>
        </div>


        {/* TURMA INFO BAR */}
        {selectedSerie && turmasDaSerie.length > 0 && (
          <div style={{
            padding: '6px 12px', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', flexShrink: 0,
            borderBottom: '1px solid var(--border-color, #e5e7eb)',
            background: 'var(--bg-card, white)', gap: 8, overflowX: 'auto',
          }}>
            {/* Esquerda: identidade da turma selecionada */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, whiteSpace: 'nowrap' }}>
              {selectedTurma && (<>
                <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{selectedTurma.nome}</span>
                <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                  {selectedTurma.serie} | {selectedTurma.turnoOperacional?.nome || '-'} | {selectedTurma.anoLetivo}
                </span>
                {isDirty && (
                  <span style={{
                    display: 'flex', alignItems: 'center', gap: 3,
                    fontSize: '0.65rem', fontWeight: 700, color: '#d97706',
                    background: '#fef3c7', padding: '2px 6px', borderRadius: 4,
                  }}><AlertTriangle size={10} /> Não salvo</span>
                )}
              </>)}
            </div>

            {/* Direita: tabs de turma */}
            <div style={{ display: 'flex', gap: 4, overflowX: 'auto', flexShrink: 0 }}>
              {turmasDaSerie.map(t => {
                const isAct = t.id === selectedTurmaId;
                const isSaved = savedTurmas.has(t.id);
                return (
                  <button key={t.id}
                    ref={el => { if (el) void turmaRefs.current.set(t.id, el); else void turmaRefs.current.delete(t.id); }}
                    onClick={() => navigateTo(t)}
                    style={{
                      height: 26, padding: '0 10px', borderRadius: 6, whiteSpace: 'nowrap',
                      fontWeight: isAct ? 700 : 500, fontSize: '0.74rem',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
                      transition: 'all .15s', flexShrink: 0,
                      border: isAct ? 'none' : '1px solid var(--border-color, #e5e7eb)',
                      background: isAct ? 'linear-gradient(135deg,#2563eb,#3b82f6)' : isSaved ? '#f0fdf4' : 'transparent',
                      color: isAct ? 'white' : isSaved ? '#16a34a' : '#6b7280',
                      boxShadow: isAct ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
                    }}>
                    {isSaved && <CheckCircle2 size={10} />}
                    {t.nome}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* GRADE TABLE */}
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px', position: 'relative' }}>

          {/* EMPTY STATE: nenhuma turma selecionada */}
          {!selectedTurma && !loadingGrade && (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              height: '100%', minHeight: 280, gap: 16, textAlign: 'center',
            }}>
              <div style={{
                background: 'linear-gradient(135deg,#eff6ff,#e0e7ff)', borderRadius: 20,
                padding: '32px 48px', border: '1.5px dashed #93c5fd', maxWidth: 400,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
              }}>
                <div style={{ background: '#2563eb', borderRadius: 14, padding: 14, display: 'inline-flex' }}>
                  <Calendar size={28} style={{ color: 'white' }} />
                </div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '1rem', color: '#1e40af' }}>
                  Selecione uma Série / Ano
                </p>
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.5 }}>
                  Escolha uma série na linha acima e em seguida
                  uma <strong>Turma</strong> para visualizar e editar a grade de horários.
                </p>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                  <span style={{
                    background: '#e0e7ff', color: '#3730a3', fontSize: '0.72rem',
                    fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                  }}>Série / Ano ↑</span>
                  <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>→</span>
                  <span style={{
                    background: '#dbeafe', color: '#1d4ed8', fontSize: '0.72rem',
                    fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                  }}>Turma ↑</span>
                </div>
              </div>
            </div>
          )}

          {/* OVERLAY: Alterações não salvas */}
          {showDirtyOverlay && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 10,
              background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(3px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'fadeIn .18s ease',
            }}>
              <style>{`
                @keyframes fadeIn { from { opacity:0; transform:scale(.97); } to { opacity:1; transform:scale(1); } }
              `}</style>
              <div style={{
                background: 'var(--bg-card, white)', borderRadius: 16, padding: '28px 32px',
                maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                display: 'flex', flexDirection: 'column', gap: 16,
                animation: 'fadeIn .2s ease',
              }}>
                {/* Icon + Título */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                    background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <AlertTriangle size={22} style={{ color: '#d97706' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--text-main)' }}>Alterações não salvas</div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>
                      Grade da turma <strong>{selectedTurma?.nome}</strong>
                    </div>
                  </div>
                </div>

                {/* Mensagem */}
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#374151', lineHeight: 1.5 }}>
                  Você modificou esta grade mas ainda não salvou.
                  O que deseja fazer com essas alterações?
                </p>

                {/* Aviso descartar */}
                <div style={{
                  background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8,
                  padding: '8px 12px', fontSize: '0.75rem', color: '#9a3412',
                  display: 'flex', alignItems: 'flex-start', gap: 6,
                }}>
                  <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                  Se abrir outra turma sem salvar, as alterações serão descartadas automaticamente.
                </div>

                {/* Botões */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button onClick={dismissOverlay} style={{
                    padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)',
                    background: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#6b7280',
                  }}>
                    ← Voltar
                  </button>
                  <button onClick={discardAndProceed} style={{
                    padding: '8px 16px', borderRadius: 8, border: '1px solid #fca5a5',
                    background: '#fef2f2', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#dc2626',
                  }}>
                    Descartar
                  </button>
                  <button onClick={saveAndProceed} disabled={saving || gradeTemErros} style={{
                    padding: '8px 20px', borderRadius: 8, border: 'none',
                    background: 'linear-gradient(135deg,#16a34a,#22c55e)', color: 'white',
                    cursor: (saving || gradeTemErros) ? 'not-allowed' : 'pointer', fontSize: '0.82rem', fontWeight: 700,
                    display: 'flex', alignItems: 'center', gap: 6, opacity: (saving || gradeTemErros) ? 0.7 : 1,
                  }}>
                    <Save size={14} /> {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {loadingGrade ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#9ca3af' }} />
              <p style={{ color: '#9ca3af', marginTop: 12 }}>Carregando grade...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : horarios.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, background: '#f9fafb', borderRadius: 12, border: '1px dashed #d1d5db' }}>
              <Calendar size={40} style={{ color: '#d1d5db', margin: '0 auto 16px' }} />
              <p style={{ color: '#6b7280', fontWeight: 600, margin: '0 0 8px' }}>Nenhum horário registrado</p>
              <button onClick={gerarGrade} style={{
                background: '#2563eb', color: 'white', border: 'none', borderRadius: 8,
                padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
              }}>Gerar Grade Agora</button>
            </div>
          ) : (
            <div className="dt-container" style={{ overflow: 'auto', maxHeight: '100%' }}>
              {slotsPreenchidos === 0 && selectedTurma && (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', margin: '0 0 10px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: '0.74rem', color: '#1e40af', fontWeight: 600 }}>
                    💡 <strong>Orientação:</strong> Clique em <strong>"Preencher Automaticamente"</strong> ou <strong>"Gerar Grade"</strong> para distribuir as disciplinas da BNCC e professores sem choques de horário.
                  </span>
                  <button
                    type="button"
                    data-testid="btn-auto-preencher-grade"
                    onClick={gerarGrade}
                    style={{
                      background: '#2563eb', color: 'white', border: 'none', borderRadius: 6,
                      padding: '5px 12px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
                      display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
                      boxShadow: '0 1px 4px rgba(37,99,235,0.2)',
                    }}
                  >
                    <RefreshCw size={11} /> Preencher Automaticamente
                  </button>
                </div>
              )}
              <table className="dt-table" style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'separate', borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th style={{
                      position: 'sticky', top: 0, left: 0, zIndex: 6,
                      width: 80, minWidth: 80, textAlign: 'left', padding: '8px',
                      background: 'var(--bg-card, #f8fafc)', borderBottom: '2px solid var(--border-color, #e2e8f0)',
                    }}>AULA</th>
                    <th style={{
                      position: 'sticky', top: 0, left: 80, zIndex: 6,
                      width: 70, minWidth: 70, textAlign: 'center', padding: '8px 6px',
                      background: 'var(--bg-card, #f8fafc)', borderBottom: '2px solid var(--border-color, #e2e8f0)',
                      borderRight: '2px solid var(--border-color, #e2e8f0)',
                    }}>HORÁRIO</th>
                    {diasPresentes.map((d) => (
                      <th key={d} style={{
                        position: 'sticky', top: 0, zIndex: 4,
                        textAlign: 'center', padding: '8px 12px', minWidth: 150,
                        background: 'var(--bg-card, #f8fafc)', borderBottom: '2px solid var(--border-color, #e2e8f0)',
                      }}>{DIA_LABELS[d] || d}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: maxAulas }, (_, i) => i + 1).map((n) => {
                    const sampleSlot = horarios.find((h) => h.numeroAula === n);
                    const isIntervalo = sampleSlot?.tipo === 'INTERVALO';
                    const descricaoAula = sampleSlot?.descricao || (isIntervalo ? 'Recreio' : `${n}ª Aula`);
                    const duracao = calcDuracao(sampleSlot?.horaInicio, sampleSlot?.horaFim);

                    // Linha de INTERVALO compacta
                    if (isIntervalo) {
                      return (
                        <tr key={n} style={{
                          background: '#fef3c7',
                          borderBottom: '2px solid var(--border-color, #e2e8f0)',
                        }}>
                          <td style={{
                            position: 'sticky', left: 0, zIndex: 3,
                            padding: '4px 8px', fontWeight: 700,
                            fontSize: '0.76rem', color: '#92400e', background: '#fef3c7',
                          }}>{descricaoAula}</td>
                          <td style={{
                            position: 'sticky', left: 80, zIndex: 3,
                            padding: '4px 6px', textAlign: 'center',
                            fontSize: '0.68rem', color: '#92400e', fontWeight: 600,
                            background: '#fef3c7', borderRight: '2px solid var(--border-color, #e2e8f0)',
                          }}>{duracao || `${sampleSlot?.horaInicio}-${sampleSlot?.horaFim}`}</td>
                          {diasPresentes.map((dia) => (
                            <td key={dia} style={{ padding: '4px', textAlign: 'center', background: '#fef3c7' }}>
                              <span style={{ fontSize: '0.68rem', color: '#b45309', fontWeight: 500 }}>-</span>
                            </td>
                          ))}
                        </tr>
                      );
                    }

                    // Linha de AULA normal
                    return (
                      <tr key={n} className="dt-row" style={{
                        borderBottom: '2px solid var(--border-color, #e2e8f0)',
                      }}>
                        {/* Coluna AULA */}
                        <td style={{
                          position: 'sticky', left: 0, zIndex: 2,
                          padding: '8px 8px', verticalAlign: 'middle',
                          background: 'var(--bg-card, #f8fafc)', width: 80, minWidth: 80,
                        }}>
                          <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>
                            {descricaoAula}
                          </span>
                        </td>

                        {/* Coluna HORARIO */}
                        <td style={{
                          position: 'sticky', left: 80, zIndex: 2,
                          padding: '8px 6px', verticalAlign: 'middle',
                          borderRight: '2px solid var(--border-color, #e2e8f0)',
                          background: 'var(--bg-card, #f8fafc)', textAlign: 'center', width: 70, minWidth: 70,
                        }}>
                          {sampleSlot?.horaInicio && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <span style={{ fontSize: '0.7rem', color: '#1e293b', fontWeight: 600 }}>{sampleSlot.horaInicio}</span>
                              <span style={{ fontSize: '0.7rem', color: '#1e293b', fontWeight: 600 }}>{sampleSlot.horaFim}</span>
                            </div>
                          )}
                        </td>

                        {/* Colunas dos dias */}
                        {diasPresentes.map((dia) => {
                          const slot = horarios.find((h) => h.diaSemana === dia && h.numeroAula === n);
                          if (!slot) return <td key={dia} style={{ background: '#f9fafb', textAlign: 'center' }}>-</td>;

                          const profsFiltered = getProfsForMateria(slot);
                          const turnoOpId =
                            selectedTurma?.turnoOperacionalId ??
                            selectedTurma?.turnoOperacional?.id ??
                            null;
                          const materiasDisponiveis = selectedTurma
                            ? materiasAtivas.filter((m) => {
                                if (m.id === slot.materiaId) return true;
                                return materiaTemProfessorDisponivel(
                                  m.id,
                                  profsAtivos,
                                  ocupacaoEfetiva,
                                  {
                                    diaSemana: slot.diaSemana,
                                    numeroAula: slot.numeroAula,
                                    horaInicio: slot.horaInicio,
                                    horaFim: slot.horaFim,
                                    excludeSlot: {
                                      turmaId: selectedTurma.id,
                                      diaSemana: slot.diaSemana,
                                      numeroAula: slot.numeroAula,
                                    },
                                    turnoOperacionalId: turnoOpId,
                                    slotProfessorId: slot.professorId,
                                  },
                                );
                              })
                            : materiasAtivas;
                          const materiaRef =
                            materiasDisponiveis.find((m) => m.id === slot.materiaId) ||
                            materiasAtivas.find((m) => m.id === slot.materiaId);
                          const materiaCor = slot.materiaId
                            ? resolveMateriaCorVisual({
                                corVisual: materiaRef?.corVisual,
                                nome: materiaRef?.nome,
                                codigo: materiaRef?.codigo,
                              })
                            : null;
                          const profConflito =
                            !!slot.professorId &&
                            !!selectedTurma &&
                            !!professorOcupadoNoSlot(ocupacaoEfetiva, {
                              professorId: slot.professorId,
                              diaSemana: slot.diaSemana,
                              numeroAula: slot.numeroAula,
                              horaInicio: slot.horaInicio,
                              horaFim: slot.horaFim,
                              excludeSlot: {
                                turmaId: selectedTurma.id,
                                diaSemana: slot.diaSemana,
                                numeroAula: slot.numeroAula,
                              },
                              turnoOperacionalId: turnoOpId,
                            });

                          return (
                            <td key={dia} style={{ padding: 4, minWidth: 150 }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <select
                                  style={materiaSelectStyle(materiaCor, !!slot.materiaId)}
                                  value={slot.materiaId || ''}
                                  onChange={(e) => updateSlot(slot.id, 'materiaId', e.target.value)}
                                  title={
                                    materiaRef?.nome ||
                                    'Só matérias com professor livre neste horário'
                                  }
                                >
                                  <option value="">Matéria...</option>
                                  {materiasDisponiveis.map((m) => (
                                    <option key={m.id} value={m.id}>{m.nome}</option>
                                  ))}
                                </select>
                                <select
                                  style={{
                                    fontSize: '0.74rem',
                                    padding: '5px 6px',
                                    borderRadius: 5,
                                    border: profConflito
                                      ? '1px solid #f87171'
                                      : '1px solid var(--border-color, #e5e7eb)',
                                    background: profConflito
                                      ? '#fef2f2'
                                      : slot.professorId
                                        ? '#f0fdf4'
                                        : '#f8fafc',
                                    color: slot.professorId ? '#0f172a' : '#94a3b8',
                                    fontWeight: slot.professorId ? 600 : 400,
                                    cursor: slot.materiaId ? 'pointer' : 'not-allowed',
                                    opacity: slot.materiaId ? 1 : 0.55,
                                    width: '100%',
                                  }}
                                  value={slot.professorId || ''}
                                  onChange={(e) => updateSlot(slot.id, 'professorId', e.target.value)}
                                  disabled={!slot.materiaId}
                                  title={
                                    profConflito
                                      ? 'Professor em conflito de horário em outra turma'
                                      : undefined
                                  }
                                >
                                  <option value="">Professor...</option>
                                  {profsFiltered.map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {doisNomes(nomeProfessorDisplay(p.nome))}
                                    </option>
                                  ))}
                                </select>
                                {profConflito && (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 3,
                                      fontSize: '0.62rem',
                                      fontWeight: 700,
                                      color: '#b91c1c',
                                    }}
                                  >
                                    <AlertTriangle size={10} /> Conflito
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{
          padding: '10px 14px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexShrink: 0,
          borderTop: '1px solid var(--border-color, #e5e7eb)',
          background: 'var(--bg-card, white)', gap: 6, flexWrap: 'wrap',
        }}>
          <button onClick={goPrev} disabled={flatIndex <= 0} style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px',
            borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)',
            background: 'none', cursor: flatIndex <= 0 ? 'not-allowed' : 'pointer',
            opacity: flatIndex <= 0 ? 0.4 : 1, fontSize: '0.78rem', fontWeight: 600,
          }}><ChevronLeft size={14} /> <span className="hidden sm:inline">Turma</span> Anterior</button>

          {/* Centro: ações da turma atual */}
          {selectedTurma ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="hidden md:inline" style={{ fontSize: '0.72rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                {slotsPreenchidos}/{totalAulas} preenchidos
              </span>
              <button
                data-testid="btn-gerar-grade"
                onClick={gerarGrade}
                style={{
                  background: '#2563eb', color: 'white', border: 'none', borderRadius: 8,
                  padding: '6px 12px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                <RefreshCw size={12} /> <span className="hidden sm:inline">{horarios.length > 0 ? 'Recriar' : 'Gerar'}</span> Grade
              </button>
              <button
                data-testid="btn-salvar-grade"
                onClick={salvarGrade}
                disabled={saving || !isDirty || gradeTemErros}
                style={{
                  background: (isDirty && !gradeTemErros) ? '#16a34a' : '#d1d5db', color: 'white', border: 'none',
                  borderRadius: 8, padding: '6px 14px', cursor: (isDirty && !gradeTemErros) ? 'pointer' : 'not-allowed',
                  fontSize: '0.76rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                <Save size={12} /> {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          ) : (
            <span style={{ fontSize: '0.74rem', color: '#d1d5db' }}>Nenhuma turma</span>
          )}

          {flatIndex < turmasOrdenadas.length - 1 ? (
            <button
              data-testid="btn-proxima-turma-grade"
              onClick={goNext}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '6px 14px',
                borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                color: 'white', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700,
                boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
              }}
            >
              Próxima <span className="hidden sm:inline">Turma</span> <ChevronRight size={14} />
            </button>
          ) : (
            <button
              data-testid="btn-finalizar-grade"
              onClick={handleClose}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '6px 14px',
                borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg, #059669, #10b981)',
                color: 'white', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700,
                boxShadow: '0 2px 8px rgba(5,150,105,0.3)',
              }}
            >
              <CheckCircle2 size={14} /> Finalizar
            </button>
          )}
        </div>
      </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '8px',
    }}>
      {content}
    </div>
  );
}
